import React from "react";
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Stack,
  Typography,
} from "@mui/material";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import PersonSearchOutlinedIcon from "@mui/icons-material/PersonSearchOutlined";
import { alpha, useTheme } from "@mui/material/styles";
import { formatCapturedAt } from "./displayHelpers";
import { poolsHaveResults } from "./navigation";
import {
  accentColor,
  ctaContainedSx,
  ctaOutlinedSx,
  interactiveSurfaceSx,
  isDarkMode,
} from "./resultatsTheme";
import type { ResultsDoc } from "./types";
import type { ResultsView } from "./navigation";

interface OverviewViewProps {
  doc: ResultsDoc;
  onNavigate: (view: ResultsView) => void;
  onFocusSearch?: () => void;
}

export default function OverviewView({
  doc,
  onNavigate,
  onFocusSearch,
}: OverviewViewProps): React.ReactElement {
  const theme = useTheme();
  const isDark = isDarkMode(theme);
  const captured = formatCapturedAt(doc.sourceCapturedAt);
  const hasPoolResults = poolsHaveResults(doc.pools);
  const hasPools = doc.pools.length > 0;
  const hasBracket = doc.rounds.length > 0;

  const poolsTitle = !hasPools
    ? "Poules"
    : hasPoolResults
      ? "Classements des poules"
      : "Tirage des poules";
  const poolsDescription = !hasPools
    ? "Tirage non publié pour le moment"
    : hasPoolResults
      ? `${doc.pools.length} poule${doc.pools.length > 1 ? "s" : ""} — classements et rencontres`
      : `${doc.pools.length} poule${doc.pools.length > 1 ? "s" : ""} — composition du tirage`;

  const navCards: {
    view: ResultsView;
    title: string;
    description: string;
    icon: React.ReactNode;
    disabled?: boolean;
    primary?: boolean;
  }[] = [
    {
      view: "pools",
      title: poolsTitle,
      description: poolsDescription,
      icon: <GroupsOutlinedIcon fontSize="small" />,
      disabled: !hasPools,
      primary: hasPools,
    },
    {
      view: "bracket",
      title: "Tableau final",
      description: hasBracket
        ? `${doc.rounds.length} tour${doc.rounds.length > 1 ? "s" : ""} — résultats éliminatoires`
        : "Pas encore publié",
      icon: <AccountTreeOutlinedIcon fontSize="small" />,
      disabled: !hasBracket,
      primary: hasBracket && !hasPools,
    },
    {
      view: "player",
      title: "Trouver un joueur",
      description: "Nom, club ou licence → poule et parcours",
      icon: <PersonSearchOutlinedIcon fontSize="small" />,
    },
  ];

  return (
    <Stack spacing={2.75}>
      <Box>
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ lineHeight: 1.6, maxWidth: 36 * 16 }}
        >
          Tirage, classements au fil des scores, puis tableau final.
          {captured ? ` Dernière mise à jour : ${captured}.` : ""}
        </Typography>
      </Box>

      <Box
        sx={{
          display: "grid",
          gap: 1.5,
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(3, minmax(0, 1fr))",
          },
        }}
      >
        {navCards.map((card) => (
          <Card
            key={card.view}
            elevation={0}
            sx={{
              ...interactiveSurfaceSx(theme),
              borderColor:
                card.primary && !card.disabled
                  ? alpha(theme.palette.secondary.main, isDark ? 0.5 : 0.4)
                  : undefined,
              opacity: card.disabled ? 0.5 : 1,
            }}
          >
            <CardActionArea
              disabled={card.disabled}
              onClick={() => {
                if (card.view === "player" && onFocusSearch) {
                  onFocusSearch();
                  return;
                }
                onNavigate(card.view);
              }}
              sx={{ height: "100%", borderRadius: "inherit" }}
            >
              <CardContent sx={{ p: 2.1 }}>
                <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: 2,
                      display: "grid",
                      placeItems: "center",
                      color: accentColor(theme),
                      bgcolor: alpha(
                        isDark
                          ? theme.palette.secondary.main
                          : theme.palette.primary.main,
                        isDark ? 0.18 : 0.1
                      ),
                    }}
                  >
                    {card.icon}
                  </Box>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 800, letterSpacing: "-0.015em" }}
                  >
                    {card.title}
                  </Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  {card.description}
                </Typography>
              </CardContent>
            </CardActionArea>
          </Card>
        ))}
      </Box>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25}>
        <Button
          variant="contained"
          color={isDark ? "secondary" : "primary"}
          disabled={!hasPools}
          onClick={() => onNavigate("pools")}
          sx={ctaContainedSx(theme)}
        >
          {hasPoolResults ? "Voir les classements" : "Voir le tirage"}
        </Button>
        <Button
          variant={hasBracket && !hasPools ? "contained" : "outlined"}
          color={isDark ? "secondary" : "primary"}
          disabled={!hasBracket}
          onClick={() => onNavigate("bracket")}
          sx={
            hasBracket && !hasPools
              ? ctaContainedSx(theme)
              : ctaOutlinedSx(theme)
          }
        >
          Voir le tableau final
        </Button>
      </Stack>
    </Stack>
  );
}
